<template>
  <a-modal
    v-model:open="visible"
    :title="modalTitle"
    width="574px"
    :mask-closable="false"
    centered
    class="hubspot-event-modal"
    @cancel="handleCancel"
    :footer="null"
  >
    <!-- Steps -->
    <a-steps progress-dot :current="currentStep" size="small" class="steps">
      <a-step title="Seleccionar contactos" />
      <a-step v-if="showResults" title="Resultados" />
      <a-step v-if="showLastStep" title="Programar hora" />
    </a-steps>

    <!-- Paso 1: Seleccionar contactos -->
    <div v-if="currentStep === 0" class="step-content">
      <div class="step-header">
        <h3 class="step-title" v-if="type == HubspotIntegrationType.Export">
          Define cuántos contactos se exportarán
        </h3>
        <h3 class="step-title" v-else>
          Detecta a los contactos a importar
        </h3>
        <p class="step-hint" v-html="description" /> 
          <!-- Por defecto, se seleccionaron todos los contactos creados desde BeexCC con el campo
          “Nombres” completado. Recuerda que <b>la {{ actionText }} será a una misma cuenta de HubSpot.</b> -->
        
      </div>

      <div :class="!isImport ? 'card-border' : ''">
        <div v-if="!isImport">
          <strong>Se han seleccionado </strong>
          <strong class="count_contacts">
            <span v-if="!loadingCount">{{ formatCount(formState.dataCount ?? dataCount) }} </span>
            <LoadingOutlined v-else style="color: #1677ff; margin-left: 3px" />
            contactos de BeexCC
            <InfoCircleOutlined style="color: #1677ff; margin-left: 3px" />
          </strong>
        </div>
        <div v-if="!isImport" class="detail">
          De: Todas las empresas y todas sus campañas
        </div>

        <a-divider
          class="divider"
          v-if="!isImport" 
        />

        <a-form layout="vertical" :model="formState">
          <entity-picker
            :label="isImport ? 'Seleccionar empresas:' : 'Filtrar por empresas'"
            :required="isImport"
            :help="isImport ? '' : 'Se seleccionarán los contactos de todas las campañas de estas empresas.'"
            :value="formState.entity"
            mode="multiple"
            :for-hubspot-import="isImport"
            @update:value="formState.entity = $event;"
          />

          <campaign-picker
            :label="isImport ? 'Seleccionar campañas:' : 'Filtrar por campañas'"
            :required="isImport"
            :help="isImport ? 
              'Campañas cuyas plantillas de contactos tienen todas las columnas requeridas por BeexCC homologadas con HubSpot.' :
              'Solo se seleccionarán los contactos de estas campañas.'"
            :value="formState.portfolio"
            :for-hubspot-import="isImport"
            @update:value="formState.portfolio = $event"
            :entity="formState.entity"
            onlyWithEntity
            multiple
            :showAll="false"
            :disabled="formState.entity?.length == 0"
          />
        </a-form>
      </div>
    </div>

    <!-- Paso 2: Programar hora -->
    <div v-else-if="currentStep === 1 && showResults" class="step-content--result">
      <div>
        <div class="status-icon success">
          <img width="56px" :src="formState?.dataCount > 0 ? CheckIcon : SearchIcon" alt="status-icon" />
        </div>

        <h3 class="step-title">
          Se han detectado {{ formState.dataCount }} de {{ formState.dataCountAvailable }}
          contactos que son válidos para importar
        </h3>

        <p class="description">
          De {{ formState.entity?.length || 0 }} empresas y {{ formState.portfolio?.length || 0 }} campañas, se detectaron
          <span class="bold-600">{{ formState.dataCount }} contactos válidos</span> para importar desde HubSpot
          <span v-if="formState?.dataCount > 0">
            , al tener completas todas las columnas requeridas por
            BeexCC. Recuerda que la 
            <span class="bold-600">importación respetará su asignación por empresa y campaña.</span>
          </span>
          <span v-else>
            . No se podrá crear el evento de importación. 
            <span class="bold-600">
              Revisa que tus contactos en HubSpot tengan completas:
            </span><br/>
            <ul class="list">
              <li>Las columnas Empresa y Campaña.</li>
              <li>
                Las columnas homologadas con las marcadas como Dato requerido en las plantillas de contactos de BeexCC, 
                según las empresas y campañas deseadas.
              </li>
            </ul>
          </span>
        </p>
      </div>
    </div>

    <!-- Paso 2/3: Programar hora -->
    <div v-else class="step-content">
      <div class="step-header">
        <h3 class="step-title">Programa la hora de {{ actionText }}</h3>
        <p class="step-hint">
          Selecciona una hora entre 23:00 y 4:00 en la que se {{ actionText }} todos los contactos seleccionados.
          Esta hora debe ser <strong>posterior a la hora actual</strong> para continuar y guardar.
        </p>
      </div>

      <a-form layout="vertical">
        <a-form-item
          label="Hora de exportación"
          required
          :validate-status="timeError ? 'error' : ''"
          :help="timeError || ''"
        >
          <a-time-picker
            format="HH:mm"
            valueFormat="HH:mm"
            v-model:value="formState.hours"
            placeholder="Seleccionar hora"
            style="width: 100%"
            :disabled-hours="disabledHours"
            :disabled-minutes="disabledMinutes"
            :show-now="false"
          />
          <div v-if="scheduledText" class="date-schedule">
            <strong>Fecha y hora a programar:</strong><br/>
            {{ scheduledText }}
          </div>
        </a-form-item>
      </a-form>

      <a-alert
        type="warning"
        show-icon
        class="alert-warning"
      >
        <template #message>
          Al continuar, el evento aún no se programará. Esto pasará después de guardar. 
          <strong>Tampoco podrás editar la hora ni los contactos, solo podrás eliminar el evento.</strong>
        </template>
      </a-alert>
    </div>

    <!-- Footer -->
    <div class="footer">
      <a-button @click="() => currentStep === 1 ? currentStep-- : handleCancel()">Cancelar</a-button>
      <a-tooltip>
        <template #title v-if="disabledImport">
          Completa todos los campos requeridos para continuar.
        </template>
        <a-button
          type="primary"
          :loading="saving || loadingCount"
          :disabled="(disabledDate && !isImport) || loadingCount || disabledImport || !handleValidCountImport"
          @click="() => 
            (currentStep === 0 || (currentStep == 1 && isImport && formState.dataCount > 0)) ? 
            nextStep() : handleSave()"
        >
          {{ isImport && currentStep == 0 ? 
            (loadingCount ? 'Detectando contactos' : 'Continuar y detectar') : 
            isFinish ? 'Finalizar' : 'Continuar' 
          }}
        </a-button>
      </a-tooltip>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons-vue";
import { HubspotIntegrationType, IntegrationHubspotTypeCode, IntegrationTypeSync, IntegrationTypeText } from "@/app/integrations/utils/enums";
import CampaignPicker from "@/app/shared/components/pickers/CampaignPicker.vue";
import EntityPicker from "@/app/shared/components/pickers/EntityPicker.vue";
import dayjs from "dayjs";
import { hubspotManager } from "../../services/HubspotManager";
import httpManager from "@/app/shared/utils/httpManager";
import { debounce } from "lodash";
import CheckIcon from "@/app/integrations/assets/check_icon.png?url";
import SearchIcon from "@/app/integrations/assets/search_icon.png?url";

type Props = {
  visible: boolean;
  type: HubspotIntegrationType;
  dataCount: number;
  saving: boolean;
  isFinishAction?: boolean;
};

const props = defineProps<Props>();
const emit = defineEmits(["update:visible", "save"]);

const visible = ref(props.visible);

const MIN_HOUR = 23;
const MAX_HOUR = 4;

const currentStep = ref(0);
// const saving = ref(false);
const exportTime = ref<dayjs.Dayjs | null>(null);
const timeError = ref<string | null>(null);

const formState = ref({
  entity: [] as string[],
  portfolio: [] as string[],
  hours: '' as string,
  scheduled: '' as string,
  dataCount: 0 as number,
  dataCountAvailable: null as number | null,
  contacts: [] as string[],
});

const isImport = computed(() => props.type == HubspotIntegrationType.Import);

const resetForm = () => {
  currentStep.value = 0;
  formState.value = {
    entity: [],
    portfolio: [],
    hours: '',
    scheduled: '',
    dataCount: 0,
    dataCountAvailable: null as number | null,
    contacts: [],
  }
};

const loadingCount = ref(false);

const checkedContactsWithFilter = async () => {
  const type = props.type;
  const entity = formState.value.entity.join(",");
  const portfolio = formState.value.portfolio.join(",");

  loadingCount.value = true;
  const response = await hubspotManager.getContactsExportableImportable(IntegrationHubspotTypeCode[type as HubspotIntegrationType], entity, portfolio);
  formState.value.dataCount = response?.[IntegrationTypeSync[props.type] + "_count"] || 0;
  formState.value.contacts = response?.[IntegrationTypeSync[props.type]] || 0;
  formState.value.dataCountAvailable = response?.[IntegrationTypeSync[props.type] + "_count_available"] || 0;
  loadingCount.value = false;
}

watch(
  () => props.visible, 
  (val) => {
    visible.value = val
    if (val) {
      resetForm()
      console.log('props visible isImport', isImport.value, val);
      if (!isImport.value)
        checkedContactsWithFilter()
    }
  },
  { immediate: true }
);
watch(visible, (val) => emit("update:visible", val));


const disabledImport = computed(() => isImport.value && (formState.value.entity?.length == 0 || formState.value.portfolio?.length == 0));

const showResults = computed(() => props.type == HubspotIntegrationType.Import);
const showLastStep = computed(() => props.type == HubspotIntegrationType.Export || formState.value.dataCount > 0);

const isFinish = computed(() => {
  if (isImport.value && props.isFinishAction) {
    if (showLastStep.value) {
      return currentStep.value === 2;
    }
    return currentStep.value === 1;
  }

  return false;
});

const disabledHours = () => {
  const allHours = Array.from({ length: 24 }, (_, i) => i);

  let allowedHours: number[] = [];

  if (MIN_HOUR <= MAX_HOUR) {
    // Caso normal, rango dentro del mismo día (ej: 2 → 6)
    allowedHours = allHours.filter(h => h >= MIN_HOUR && h <= MAX_HOUR);
  } else {
    // Caso que cruza medianoche (ej: 23 → 4)
    allowedHours = allHours.filter(h => h >= MIN_HOUR || h <= MAX_HOUR);
  }

  return allHours.filter(h => !allowedHours.includes(h));
};

const disabledMinutes = (selectedHour?: number) => {
  if (selectedHour === undefined) return [];

  // Si la hora está fuera del rango permitido → bloquear todo
  const crossesMidnight = MIN_HOUR > MAX_HOUR;
  const inRange = crossesMidnight
    ? selectedHour >= MIN_HOUR || selectedHour <= MAX_HOUR
    : selectedHour >= MIN_HOUR && selectedHour <= MAX_HOUR;

  if (!inRange) return Array.from({ length: 60 }, (_, i) => i);

  // Si es la hora máxima (ej. 4) → solo permitir minuto 0
  if (selectedHour === MAX_HOUR) {
    return Array.from({ length: 60 }, (_, i) => i).filter(m => m !== 0);
  }

  // En los demás casos → permitir todos los minutos
  return [];
};

const disabledDate = ref(true);

watch(
  [() => formState.value.hours, () => currentStep.value],
  ([hours, step]) => {
    // Si no estamos en el paso 1, no desactivar
    if (step !== 1) {
      disabledDate.value = false;
      return;
    }

    console.log(hours, 'hours');
    if (!hours) {
      disabledDate.value = true;
      return;
    }

    const selected = dayjs(hours, "HH:mm");
    if (!selected.isValid()) {
      disabledDate.value = true;
      return;
    }

    disabledDate.value = false;
  },
  { immediate: true }
);

watch(
  () => formState.value.entity,
  debounce(() => {
    formState.value.portfolio = [];
    if (!isImport.value) checkedContactsWithFilter();
  }, 500),
  { deep: true }
);

watch(
  () => formState.value.portfolio,
  debounce(() => {
    if (!isImport.value) checkedContactsWithFilter();
  }, 500),
  { deep: true }
);

const modalTitle = computed(() =>
  props.type === HubspotIntegrationType.Export
    ? "Crear evento de exportación"
    : "Crear evento de importación"
);

const actionText = computed(() =>
  props.type === HubspotIntegrationType.Export
    ? "exportar"
    : "importar"
);

const description = computed(() =>
  props.type === HubspotIntegrationType.Export
    ? "Por defecto, se seleccionaron todos los contactos creados desde BeexCC con el campo \"Nombres\" completado, pero puedes filtrarlos. La exportación será <strong>a una misma cuenta de HubSpot.</strong>"
    : "Indica qué contactos vinculados a las empresas y campañas de BeexCC desde HubSpot deseas importar. <strong>La importación respetará esa asignación</strong> y solo se importarán contactos que tengan completas las columnas requeridas por BeexCC."
);

const formatCount = (num: number) => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return num.toString();
}

const scheduledText = computed(() => {
  if (!formState.value.hours) return "";
  const date = getScheduledDate(formState.value.hours);
  return date.toLocaleString("es-PE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit"
  });
});

const getScheduledDate = (hourStr: string) => {
  const now = new Date();

  const [hour, minute] = hourStr.split(":").map(Number);

  let scheduled = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hour,
    minute,
    0,
    0
  );

  if (hour >= 0 && hour <= 4) {
    scheduled.setDate(scheduled.getDate() + 1);
  }

  if (scheduled <= now) {
    scheduled.setDate(scheduled.getDate() + 1);
  }

  return scheduled;
}

const nextStep = async () => {
  if (isImport.value && currentStep.value === 0) {
    await checkedContactsWithFilter();
  }
  currentStep.value++;
}

const handleCancel = () => {
  emit("update:visible", false)
}

const calculateSchedule = () => {
  const selectedTime = dayjs(formState.value.hours, "HH:mm");
  const now = dayjs();

  // Si la hora ya pasó hoy, usamos el día siguiente
  const scheduledDate = selectedTime.isBefore(now)
    ? dayjs().add(1, "day").hour(selectedTime.hour()).minute(selectedTime.minute()).second(0)
    : dayjs().hour(selectedTime.hour()).minute(selectedTime.minute()).second(0);

  // Guardar en formato ISO (o el que necesites)
  formState.value.scheduled = scheduledDate.format("YYYY-MM-DDTHH:mm:ss");
}

const handleValidCountImport = computed(() => {
  if (isImport.value && currentStep.value === 1 && formState.value.dataCount < 1) {
    return false;
  }
  return true;
});

const handleSave = async () => {
  if (formState.value.dataCount > 0) {
    // saving.value = true;
    calculateSchedule();
    await checkedContactsWithFilter();
    emit('save', { formState: formState.value, type: props.type });
  } else {
    handleCancel()
  }
}
</script>

<style lang="sass" scoped>
strong
  font-weight: 600
.hubspot-event-modal
  color: #262626E0
  :deep(.ant-modal-content)
    border-radius: 14px

.steps
  margin: 18px 0
  padding: 0 45px

.step-header
  text-align: center

.step-content
  margin-top: 10px
  padding: 10px 45px
  color: #000000A6
  &--result
    text-align: center
    min-height: calc(100vh - 500px)
    display: flex
    padding: 0 50px
    align-items: center


.step-title
  font-size: 20px
  font-weight: 600
  margin: 20px 0 16px 0
  color: #262626E0

.description
  font-size: 14px
  color: #000000A6

.bold-600
  font-weight: 600

.list
  text-align: left
  margin-top: 16px

.step-hint
  color: #000000A6
  font-size: 14px
  margin-bottom: 14px

.card-border
  border-radius: 0 0 8px 8px
  border: 1px solid rgba(0, 0, 0, 0.15)
  padding: 10px 20px 20px 20px
  border-radius: 8px

// .selection-summary
//   background: rgba(0, 0, 0, 0.02)
//   border: 1px solid rgba(0, 0, 0, 0.06)
//   border-radius: 12px
//   padding: 12px
//   margin-bottom: 16px

.alert-warning
  margin-top: 18px
  border-radius: 10px
  color: #262626E0

.footer
  display: flex
  justify-content: flex-end
  gap: 10px
  margin-top: 20px

.count_contacts
  font-size: 16px
  color: #1677FF

.detail
  font-style: italic
  color: #00000073

.divider
  margin: 10px 0 15px

.hubspot-event-modal >label
    color: $gray-8 !important  

.date-schedule
  margin-top: 12px
  color: $gray-8
</style>
